<script lang="ts">
  import { icon } from '#app/lib/icons'
  import type { SerializedEntity } from '#entities/lib/entities'
  import Spinner from '#general/components/spinner.svelte'
  import EntityListingComment from '#listings/components/entity_listing_comment.svelte'
  import ListingPreview from '#listings/components/listing_preview.svelte'
  import { getListingsByEntityUri, type SerializedListingWithMatchingElementsAndInfos } from '#listings/lib/listings'
  import { i18n, I18n } from '#user/lib/i18n'

  const {
    entity,
  }: {
    entity: SerializedEntity
  } = $props()

  const uri = $derived(entity.uri)
  let listings: SerializedListingWithMatchingElementsAndInfos[] = $state([])

  async function getListings () {
    if (uri) {
      listings = await getListingsByEntityUri(uri) || []
    }
  }

  const [ listingsWithEntityComment, listingsWithoutEntityComment ] = $derived.by(() => {
    const _listingsWithEntityComment: SerializedListingWithMatchingElementsAndInfos[] = []
    const _listingsWithoutEntityComment: SerializedListingWithMatchingElementsAndInfos[] = []
    for (const listing of listings) {
      if (listing.elements.find(el => el.uri === uri)?.comment) {
        _listingsWithEntityComment.push(listing)
      } else {
        _listingsWithoutEntityComment.push(listing)
      }
    }
    return [ _listingsWithEntityComment, _listingsWithoutEntityComment ]
  })

  const waitingForListings = getListings()
</script>

{#await waitingForListings}
  <p class="loading">{I18n('loading')}<Spinner /></p>
{:then}
  <div class="entity-listings" class:empty={listingsWithEntityComment.length === 0}>
    <h5>{@html icon('comment-o')} {i18n('Comments')}</h5>
    {#if listingsWithEntityComment.length === 0}
      <p class="empty">
        {i18n('There is nothing here')}
      </p>
    {:else}
      <ul class="listings-list">
        {#each listingsWithEntityComment as listing, i (listing._id)}
          <EntityListingComment
            bind:listing={listingsWithEntityComment[i]}
            {uri}
            {entity}
            onRemove={() => {
              listings = listings.filter(({ _id }) => _id !== listing._id)
            }} />
        {/each}
      </ul>
    {/if}
  </div>
  <div class="entity-listings" class:empty={listingsWithoutEntityComment.length === 0}>
    <h5>{@html icon('list')} {i18n('Lists')}</h5>
    {#if listingsWithoutEntityComment.length === 0}
      <p class="empty">
        {i18n('There is nothing here')}
      </p>
    {:else}
      <ul class="listings-list">
        {#each listingsWithoutEntityComment as listing (listing._id)}
          <ListingPreview {listing} imagesLimit={8} />
        {/each}
      </ul>
    {/if}
  </div>
{/await}

<style lang="scss">
  @import "general/scss/utils";
  @import "listings/scss/listings_list";

  .entity-listings{
    background-color: $off-white;
    @include radius;
    align-self: stretch;
    &.empty{
      @include display-flex(row, center, space-between);
    }
    &:not(.empty){
      h5{
        margin-block-end: 0.5rem;
      }
    }
    /* Small screens */
    @media screen and (width < $small-screen){
      margin: 1rem 0 0;
      padding: 0.5rem;
    }
    /* Large screens */
    @media screen and (width >= $small-screen){
      margin: 0.5rem 0;
      padding: 0.5rem 1rem 1rem;
    }
  }
  .listings-list{
    @include listings-list(20rem);
  }
  h5{
    @include sans-serif;
    padding: 0.3em 0.5em;
    @include display-flex(row, center, flex-start);
    :global(.fa){
      color: $soft-grey;
      margin-inline-end: 0.5rem;
    }
    :global(.fa-comment-o){
      margin-block-end: 0.1rem;
    }
  }
  .loading{
    @include display-flex(column, center);
  }
  p.empty{
    color: $grey;
    text-align: center;
  }
</style>
